/**
 * DialIQ — Optimized Redial Engine
 *
 * API client for the time-zone aware redial system. Mirrors the backend
 * RedialController endpoints (src/controllers/redial.controller.ts).
 */
import api from "./api";
import type {
  ApproveDigestPayload,
  ApproveDigestResult,
  DailyDigest,
  DialIQConfig,
  RedialChipCounts,
  RedialListResult,
  RedialWindow,
} from "./redialService.types";

// Axios interceptor unwraps response.data — the returned value IS the envelope
interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp?: string;
}

const REDIAL_ENDPOINTS = {
  config: "/api/redial/config",
  digest: "/api/redial/digest",
  approveDigest: "/api/redial/digest/approve",
  list: "/api/redial/list",
  counts: "/api/redial/counts",
  override: (leadId: string) => `/api/redial/lead/${leadId}/override`,
  undo: (leadId: string) => `/api/redial/lead/${leadId}/undo`,
  skip: (leadId: string) => `/api/redial/lead/${leadId}/skip`,
};

// ────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────

export async function getConfig(): Promise<DialIQConfig> {
  const response = (await api.get<ApiResponse<DialIQConfig>>({
    url: REDIAL_ENDPOINTS.config,
  })) as unknown as ApiResponse<DialIQConfig>;
  return response.data;
}

export async function getDigest(
  date?: string,
  timezone?: string
): Promise<DailyDigest> {
  const qs = new URLSearchParams();
  if (date) qs.set("date", date);
  if (timezone) qs.set("tz", timezone);

  const response = (await api.get<ApiResponse<DailyDigest>>({
    url: `${REDIAL_ENDPOINTS.digest}?${qs.toString()}`,
  })) as unknown as ApiResponse<DailyDigest>;
  return response.data;
}

export async function getChipCounts(): Promise<RedialChipCounts> {
  const response = (await api.get<ApiResponse<RedialChipCounts>>({
    url: REDIAL_ENDPOINTS.counts,
  })) as unknown as ApiResponse<RedialChipCounts>;
  return response.data;
}

export async function getList(
  window: RedialWindow,
  opts?: { liveOnly?: boolean; limit?: number }
): Promise<RedialListResult> {
  const qs = new URLSearchParams({ window });
  if (opts?.liveOnly !== undefined) qs.set("liveOnly", String(opts.liveOnly));
  if (opts?.limit) qs.set("limit", String(opts.limit));

  const response = (await api.get<ApiResponse<RedialListResult>>({
    url: `${REDIAL_ENDPOINTS.list}?${qs.toString()}`,
  })) as unknown as ApiResponse<RedialListResult>;
  return response.data;
}

// ────────────────────────────────────────────────────────────
// Mutations
// ────────────────────────────────────────────────────────────

export async function approveDigest(
  payload: ApproveDigestPayload
): Promise<ApproveDigestResult> {
  const response = (await api.post<ApiResponse<ApproveDigestResult>>({
    url: REDIAL_ENDPOINTS.approveDigest,
    body: payload,
  })) as unknown as ApiResponse<ApproveDigestResult>;
  return response.data;
}

export async function setOverride(
  leadId: string,
  window: RedialWindow | null
): Promise<void> {
  await api.post({
    url: REDIAL_ENDPOINTS.override(leadId),
    body: { window },
  });
}

export async function undoAdvance(
  leadId: string,
  restoreTo: RedialWindow
): Promise<void> {
  await api.post({
    url: REDIAL_ENDPOINTS.undo(leadId),
    body: { restoreTo },
  });
}

export async function skipForToday(leadId: string): Promise<void> {
  await api.post({
    url: REDIAL_ENDPOINTS.skip(leadId),
    body: {},
  });
}

// ────────────────────────────────────────────────────────────
// Default export — gather everything
// ────────────────────────────────────────────────────────────

const redialService = {
  getConfig,
  getDigest,
  getChipCounts,
  getList,
  approveDigest,
  setOverride,
  undoAdvance,
  skipForToday,
};

export default redialService;
